/**
 * String helper utilities for the application
 */

import { Agent } from '../types/agent';

/**
 * Safely gets initials from first and last names
 * Handles empty strings and undefined values properly with TypeScript strict mode
 * @param firstName - First name
 * @param lastName - Last name
 * @returns Combined initials or empty string if both names are empty
 */
export const getInitials = (firstName: string | undefined, lastName: string | undefined): string => {
  const firstInitial = firstName && firstName.length > 0 ? firstName[0] : '';
  const lastInitial = lastName && lastName.length > 0 ? lastName[0] : '';
  return (firstInitial || '') + (lastInitial || '');
};

/**
 * Gets initials from an agent object
 * Tries displayName first, then firstName/lastName, then name, then email
 * @param agent - Agent object
 * @returns Initials (e.g., "JP")
 */
export const getAgentInitials = (agent: Agent | undefined | null): string => {
  if (!agent) return '';
  
  // Try displayName first
  if (agent.displayName) {
    const parts = agent.displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return getInitials(parts[0], parts[parts.length - 1]);
    } else if (parts.length === 1 && parts[0]) {
      return parts[0][0]?.toUpperCase() || '';
    }
  }
  
  // Try firstName and lastName
  if (agent.firstName || agent.lastName) {
    return getInitials(agent.firstName, agent.lastName);
  }
  
  // Fall back to name property
  if (agent.name) {
    const parts = agent.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return getInitials(parts[0], parts[parts.length - 1]);
    } else if (parts.length === 1 && parts[0]) {
      return parts[0][0]?.toUpperCase() || '';
    }
  }
  
  // Fall back to email
  if (agent.email) {
    return agent.email[0]?.toUpperCase() || '';
  }
  
  return '';
};

/**
 * Gets display name from an agent object
 * Tries displayName first, then firstName/lastName, then name, then email
 * @param agent - Agent object
 * @returns Display name
 */
export const getAgentDisplayName = (agent: Agent | undefined | null): string => {
  if (!agent) return '';
  
  // Try displayName first
  if (agent.displayName?.trim()) {
    return agent.displayName.trim();
  }
  
  // Try firstName and lastName
  const firstName = agent.firstName?.trim() || '';
  const lastName = agent.lastName?.trim() || '';
  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }
  
  // Fall back to name property
  if (agent.name?.trim()) {
    return agent.name.trim();
  }
  
  // Fall back to email
  return agent.email || '';
};

/**
 * Safely gets the first character of a string
 * @param str - Input string
 * @returns First character or empty string
 */
export const getFirstChar = (str: string | undefined): string => {
  return str && str.length > 0 ? str[0] || '' : '';
};

/**
 * Safely truncates a string with ellipsis
 * @param str - Input string
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis if needed
 */
export const truncateWithEllipsis = (str: string | undefined, maxLength: number): string => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

/**
 * Formats a name with first initial of last name
 * @param firstName - First name
 * @param lastName - Last name
 * @returns Formatted name (e.g., "John D.")
 */
export const formatNameWithInitial = (firstName: string | undefined, lastName: string | undefined): string => {
  if (!firstName) return '';
  const lastInitial = lastName && lastName.length > 0 ? lastName[0] : '';
  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
};

/**
 * Formats an agent's name with first initial of last name
 * Handles different name formats (displayName, firstName/lastName, name, or email)
 * @param agent - Agent object
 * @returns Formatted name (e.g., "Jordan P.")
 */
export const formatAgentNameWithInitial = (agent: Agent | undefined | null): string => {
  if (!agent) return '';
  
  // Try displayName first
  if (agent.displayName) {
    const parts = agent.displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return formatNameWithInitial(parts[0], parts[parts.length - 1]);
    }
    return agent.displayName;
  }
  
  // Try firstName and lastName
  if (agent.firstName || agent.lastName) {
    return formatNameWithInitial(agent.firstName, agent.lastName);
  }
  
  // Fall back to name property
  if (agent.name) {
    const parts = agent.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return formatNameWithInitial(parts[0], parts[parts.length - 1]);
    }
    return agent.name;
  }
  
  // Fall back to email
  return agent.email || '';
};
